<template>
    <div v-if="plan">
        <card-header title="Create Tax Payment Plan" icon="fa-receipt" />

        <card-body>
            <card-list>
                <user-tile
                    :card="card"
                    :user="plan.users"
                    @click="$openCard('user', {userId: plan.users.id}, card)"
                />
            </card-list>

            <card-list>
                <subheader-list-item title="Source Plan" icon="fa-money-check-alt" />
            </card-list>
            <data-row title="Plan ID">R{{ plan.id }}</data-row>
            <data-row title="Tax Amount">{{ plan.tax_amount | currency }}</data-row>
            <data-row title="Tax Paid">{{ plan.tax_paid | currency }}</data-row>
            <data-row title="Tax Balance">{{ taxBalance | currency }}</data-row>

            <card-list>
                <subheader-list-item title="Tax Payment Plan Details" icon="fa-receipt" />

                <container-list-item padded no-border>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Preset" label-position="inside">
                                <b-select expanded v-model="preset" @input="applyPreset">
                                    <option :value="null">Custom</option>
                                    <option :value="3">3 Months</option>
                                    <option :value="5">5 Months</option>
                                    <option :value="10">10 Months</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Term (Months)" label-position="inside">
                                <b-input type="number" v-model.number="term" min="1" max="36" @input="preset = null" expanded></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Fee" label-position="inside">
                                <b-input type="number" v-model.number="customFee" step="5" min="0" @input="preset = null" expanded></b-input>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Total" label-position="inside">
                                <div class="input" style="padding: 2.1rem 0.7rem 1rem;">{{ total | currency }}</div>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Monthly Payment" label-position="inside">
                                <div class="input" style="padding: 2.1rem 0.7rem 1rem;">{{ monthly | currency }}</div>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column is-5">
                            <b-field label="Deferment Month" label-position="inside">
                                <b-select expanded v-model="deferMonth">
                                    <option value="01">1 - January</option>
                                    <option value="02">2 - February</option>
                                    <option value="03">3 - March</option>
                                    <option value="04">4 - April</option>
                                    <option value="05">5 - May</option>
                                    <option value="06">6 - June</option>
                                    <option value="07">7 - July</option>
                                    <option value="08">8 - August</option>
                                    <option value="09">9 - September</option>
                                    <option value="10">10 - October</option>
                                    <option value="11">11 - November</option>
                                    <option value="12">12 - December</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column is-3">
                            <b-field label="Deferment Day" label-position="inside">
                                <div class="input" style="padding: 2.1rem 0.7rem 1rem;">1</div>
                            </b-field>
                        </div>
                        <div class="column is-4">
                            <b-field label="Deferment Year" label-position="inside">
                                <b-select expanded v-model="deferYear">
                                    <option v-for="year in deferYears" :key="year" :value="year">{{ year }}</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                </container-list-item>
            </card-list>
        </card-body>

        <card-list v-if="taxBalance > 0">
            <subheader-list-item title="Email Summary" icon="fa-envelope" />
            <container-list-item padded no-border>
                <div ref="emailSummary" style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.8;">
                    <p>Here are your payment plan options for the outstanding tax balance of <strong>{{ taxBalance | currency }}</strong>:</p>
                    <p v-for="opt in emailOptions" :key="opt.term" style="margin: 0 0 8px 0;">
                        <strong>{{ opt.term }} Months</strong> — {{ taxBalance | currency }} + {{ opt.fee | currency }} taxes/fees = <strong>{{ opt.total | currency }}</strong> total ({{ opt.monthly | currency }}/mo)
                    </p>
                </div>
                <b-button size="is-small" type="is-info" outlined @click="copyEmailSummary">
                    <i class="fas fa-copy mr-1"></i>Copy to Clipboard
                </b-button>
            </container-list-item>
        </card-list>

        <card-footer class="pt-3 pb-3 has-text-right">
            <b-button
                type="is-primary"
                @click="submit"
                :disabled="!isValid"
                :loading="submitting"
            ><i class="fas fa-check mr-2"></i>Create Tax Plan
            </b-button>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardFooter from "@/TIER/components/CardFooter";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import UserTile from "@/components/tiles/UserTile";
    import DataRow from "@/TIER/components/DataRow";

    import {client as http} from '../../http_client';
    import async from "async";

    export default {
        components: {DataRow, UserTile, ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props', 'child'],
        data() {
            return {
                plan: null,
                preset: null,
                term: null,
                customFee: 0,
                deferMonth: null,
                deferYear: null,
                submitting: false
            };
        },
        computed: {
            taxBalance() {
                if (!this.plan) return 0;
                return (parseFloat(this.plan.tax_amount) || 0) - (parseFloat(this.plan.tax_paid) || 0);
            },
            taxRate() {
                return parseFloat(this.plan?.tax_rate) || 0;
            },
            calculatedTax() {
                return Math.ceil((this.taxBalance * this.taxRate) / 5) * 5;
            },
            total() {
                return this.taxBalance + (this.customFee || 0);
            },
            monthly() {
                const t = parseInt(this.term);
                if (!t || this.total <= 0) return 0;
                return Math.ceil(this.total / t);
            },
            isValid() {
                return this.term && this.term > 0 && this.deferMonth && this.deferYear && this.taxBalance > 0;
            },
            emailOptions() {
                const flatFees = { 3: 25, 5: 40, 10: 70 };
                return [3, 5, 10].map(t => {
                    const fee = this.calculatedTax + flatFees[t];
                    const total = this.taxBalance + fee;
                    return { term: t, fee, total, monthly: Math.ceil(total / t) };
                });
            },
            deferYears() {
                const currentYear = new Date().getFullYear();
                return [currentYear, currentYear + 1, currentYear + 2];
            }
        },
        methods: {
            loadPlan() {
                this.$emit('loading', true);
                http.get('payment-plans/' + this.props.planId, {force: true}).then(response => {
                    this.plan = response.data;
                    this.$emit('loading', false);
                }).catch(err => {
                    this.$emit('loading', false);
                });
            },
            applyPreset(val) {
                if (!val) return;
                const flatFees = { 3: 25, 5: 40, 10: 70 };
                this.term = val;
                this.customFee = this.calculatedTax + (flatFees[val] || 0);
            },
            copyEmailSummary() {
                const html = this.$refs.emailSummary.innerHTML;
                const blob = new Blob([html], { type: 'text/html' });
                const item = new ClipboardItem({ 'text/html': blob });
                navigator.clipboard.write([item]).then(() => {
                    this.$buefy.toast.open({ message: 'Copied to clipboard!', type: 'is-success' });
                }).catch(() => {
                    this.$buefy.toast.open({ message: 'Failed to copy', type: 'is-danger' });
                });
            },
            submit() {
                this.submitting = true;
                http.post('quotes/tax-plan', {
                    sourcePlanId: this.plan.id,
                    term: parseInt(this.term),
                    fee: this.customFee,
                    defer_month: this.deferMonth,
                    defer_year: this.deferYear
                }).then(response => {
                    this.submitting = false;
                    this.$buefy.toast.open({
                        message: 'Tax payment plan created successfully!',
                        type: 'is-success'
                    });
                    this.$reloadCard('pending-plans');
                    this.$closeCard(this.card);
                }).catch(err => {
                    this.submitting = false;
                    this.$buefy.toast.open({
                        message: 'Failed to create tax payment plan',
                        type: 'is-danger'
                    });
                });
            }
        },
        mounted() {
            this.loadPlan();
        }
    };
</script>
